import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useTheme } from "react-jss";

/* Helpers, constants, custom methods, internal libraries */
import { useNotificationContext } from "../../../contexts/notificationContext.js";
import { useIntegrationService } from "../../../services/integrationService.js";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { PAGE_TITLES, VERSA_LINK_SCRIPT_SRC } from "../../../constants/appConstants";
import { useLocaleContext } from "../../../contexts/localeContext.js";

/* Components */
import IntegrationsTabSkeleton from "./skeletonIntegrationsTab/index.js";

/* Styles */
import styles from "./styles.js";

const IntegrationsTab = () => {
  const theme = useTheme();
  const classes = styles({ theme });

  const locale = useLocaleContext();
  const { setNotification } = useNotificationContext();
  const { getDataSharing, getVersaLinkToken, sendProvidersToVersaLink } = useIntegrationService();
  const { initAnalytics } = useAnalytics();

  const [versaReady, setVersaReady] = useState(false);
  const [versaInitialized, setVersaInitialized] = useState(false);
  const isVersaLoading = !versaReady || !versaInitialized;
  const [showSkeleton, setShowSkeleton] = useState(true);

  useEffect(() => {
    const initUtagData = {
      channel: "content",
      pageName: PAGE_TITLES["integrations"],
    };
    initAnalytics(initUtagData, "init call", "view");

    getDataSharing("ExpenseIntegration");
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (window.VersaLink) {
      setVersaReady(true);
      return;
    }

    const src = VERSA_LINK_SCRIPT_SRC;
    if (!src) {
      // console.error("VERSA_LINK_SCRIPT_SRC env var is not set");
      return;
    }

    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener("load", () => setVersaReady(true));
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => setVersaReady(true);
    script.onerror = () => {
      setNotification(
        "error",
        <FormattedMessage id="versaLink.error.unableToLoad.title" />,
        <FormattedMessage id="versaLink.error.unableToLoad.description" />,
        true
      );
    };

    document.body.appendChild(script);

    return () => {
      script.onload = null;
      script.onerror = null;
    };
  }, [setNotification]);

  useEffect(() => {
    if (isVersaLoading) {
      setShowSkeleton(true);
      return;
    }

    const timeout = setTimeout(() => {
      setShowSkeleton(false);
    }, 350);

    return () => clearTimeout(timeout);
  }, [isVersaLoading]);

  const handleOpenVersaLink = async () => {
    if (typeof window === "undefined" || !window.VersaLink) {
      setNotification(
        "error",
        <FormattedMessage id="versaLink.error.notReady.title" />,
        <FormattedMessage id="versaLink.error.notReady.description" />,
        true
      );
      return false;
    }

    const tokenData = await getVersaLinkToken();
    if (!tokenData) return false;

    const { token } = tokenData;

    try {
      await window.VersaLink.initialize({
        linkToken: token,
        theme: {
          viewMode: "inline",
        },
        language: locale.toLowerCase(),
        whitelistOrgs: tokenData.whitelistOrgs,

        onEvent: (name, meta) => {
          // console.log("Versa inline event:", name, meta);
        },

        onSuccess: async (res) => {
          // console.log("Versa inline success:", res);

          try {
            await sendProvidersToVersaLink(res);
          } catch (e) {
            console.error("Failed to send providers to Versa endpoint:", e);
            setNotification(
              "error",
              <FormattedMessage id="versaLink.error.syncProviders.title" />,
              <FormattedMessage id="versaLink.error.syncProviders.postSuccess.description" />,
              true
            );
          }

          if (res && Array.isArray(res.changes)) {
            res.changes.forEach((change) => {
              const action =
                change.action === "enabled" ? "connected" : "disconnected";

              const integrationName =
                change.org?.name ||
                change.org?.friendlyName ||
                "Integration";

              const titleId =
                action === "connected"
                  ? "versaLink.notification.connected.title"
                  : "versaLink.notification.disconnected.title";

              const descriptionId =
                action === "connected"
                  ? "versaLink.notification.connected.description"
                  : "versaLink.notification.disconnected.description";

              setNotification(
                "success",
                <FormattedMessage id={titleId} values={{ integrationName }} />,
                <FormattedMessage id={descriptionId} values={{ integrationName }} />,
                true,
                5000
              );
            });
          }

          // getDataSharing("ExpenseIntegration");
        },

        onExit: () => {
          // console.log("Versa inline exit");
        },
      });

      window.VersaLink.open();
      return true;
    } catch (err) {
      console.error("Failed to initialize Versa Link:", err);
      setNotification(
        "error",
        <FormattedMessage id="versaLink.error.unableToOpen.title" />,
        <FormattedMessage id="versaLink.error.unableToOpen.description" />,
        true
      );
      return false;
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined" && window.VersaLink && versaInitialized) {
      try {
        window.VersaLink.exit?.();
      } catch (e) {
        console.warn("Error while exiting Versa on locale change:", e);
      }
    }
    setVersaInitialized(false);
  }, [locale]);

  useEffect(() => {
    if (!versaReady || versaInitialized) return;

    handleOpenVersaLink().then((ok) => {
      if (ok) setVersaInitialized(true);
    });
  }, [versaReady, versaInitialized]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.VersaLink && versaInitialized) {
      try {
        window.VersaLink.exit?.();
      } catch (e) {
        console.warn("Error while exiting Versa on locale change:", e);
      }
    }

    if (typeof document !== "undefined") {
      const canvas = document.getElementById("versa-canvas");
      if (canvas) {
        canvas.innerHTML = "";
      }
    }

    setVersaInitialized(false);
  }, [locale]);

  return (
    <div className={classes.integrationContent}>
      <div className={classes.integrationsTab}>
        <div className={classes.details}>
          <div tabIndex={0}>
            <h2 className={classes.title}>
              <FormattedMessage id="settings.integration.title" />
            </h2>
            <h3 className={classes.description}>
              <FormattedMessage id="settings.integration.description" />
            </h3>
          </div>
        </div>
        <div className={classes.versaWrapper}>
          <div id="versa-canvas" className={classes.versaCanvas} />
          {showSkeleton && (
            <div className={classes.versaSkeletonOverlay}>
              <IntegrationsTabSkeleton providerNumber={6} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntegrationsTab;
